import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/authContext";
import { getServicioStyle } from "../../utils/servicioColores";

// ORGANIZAR RUTA 3: asignar a cada piso de ruta 3 el/los día(s) (lunes a viernes) en que se visita.
// Antes todos eran el miércoles; un piso sin organizar sigue en miércoles. Se pueden marcar VARIOS días.
// Cada cambio vale desde la fecha que se elija (por defecto hoy) y queda en el historial: los meses pasados
// conservan los días que tenían. La lista sale agrupada por día (el primero de cada piso) y ordenada por la
// hora de entrada de la asistenta ese día, con su plan, y cuántos alumnos son A y cuántos R.
// Días: lunes = 1 ... viernes = 5 (igual que en la BBDD). Supervisora: sus pisos · admins: los de sus supervisoras.

const DIAS = [
  { n: 1, l: "L", largo: "lunes", color: "#2563eb" },
  { n: 2, l: "M", largo: "martes", color: "#7c3aed" },
  { n: 3, l: "X", largo: "miércoles", color: "#0d9488" },
  { n: 4, l: "J", largo: "jueves", color: "#ea580c" },
  { n: 5, l: "V", largo: "viernes", color: "#db2777" },
];
const dia = (n) => DIAS[n - 1];
const fmtFecha = (s) => (s ? `${s.slice(8, 10)}/${s.slice(5, 7)}/${s.slice(2, 4)}` : "");
const fmtFechaHora = (s) => (s ? `${s.slice(8, 10)}/${s.slice(5, 7)} ${s.slice(11, 16)}` : "");
// [1,4] -> "lunes y jueves" · [1,3,4] -> "lunes, miércoles y jueves"
const nombreDias = (ds) => {
  if (!ds || !ds.length) return "sin organizar (miércoles)";
  const nombres = [...ds].sort().map((n) => dia(n)?.largo || "—");
  return nombres.length === 1 ? nombres[0] : `${nombres.slice(0, -1).join(", ")} y ${nombres[nombres.length - 1]}`;
};
const mismosDias = (a, b) => a.length === b.length && [...a].sort().every((x, i) => x === [...b].sort()[i]);
const p2 = (n) => String(n).padStart(2, "0");
const isoDe = (d) => `${d.getFullYear()}-${p2(d.getMonth() + 1)}-${p2(d.getDate())}`;
const sumarDias = (s, n) => { const d = new Date(s + "T00:00:00"); d.setDate(d.getDate() + n); return isoDe(d); };
const proximoLunes = (s) => { const d = new Date(s + "T00:00:00"); return sumarDias(s, 7 - ((d.getDay() + 6) % 7)); };
const primeroMesSiguiente = (s) => { const d = new Date(s + "T00:00:00"); return isoDe(new Date(d.getFullYear(), d.getMonth() + 1, 1)); };

// Plan del piso ese día (planificación semanal del servicio): qué se hace en el piso.
const PlanDia = ({ plan, dia: n }) => {
  const x = plan?.[n];
  if (!x) return null;
  const chips = [
    x.hab && ["🛏️", `Hab ${x.hab.habitacion || x.hab.nombre || ""}`.trim(), `Limpieza de habitación: ${x.hab.nombre || ""} ${x.hab.habitacion ? `(hab ${x.hab.habitacion})` : ""}`],
    x.lavado && ["👕", "Ropa", `Lavado de ropa: ${x.lavado.nombre || ""}`],
    x.planchado && ["🧺", "Plancha", `Planchado: ${x.planchado.nombre || ""}`],
    x.banos && ["🚿", "Baños", "Baños"],
    x.salon && ["🛋️", "Salón", "Salón"],
    x.cocina && ["🍳", "Cocina", "Cocina"],
  ].filter(Boolean);
  if (!chips.length) return null;
  return <>{chips.map(([ic, t, tt]) => <span key={t} title={tt} style={st.chipPlan}>{ic} {t}</span>)}</>;
};

const OrganizarRuta3 = () => {
  const navigate = useNavigate();
  const { user: authUser } = useAuth();
  const esSupervisora = authUser?.tipo === "supervisora";

  const [pisos, setPisos] = useState(null);
  const [cambios, setCambios] = useState({});          // id_piso -> días elegidos (sin guardar)
  const [error, setError] = useState("");
  const [aviso, setAviso] = useState("");
  const [guardando, setGuardando] = useState(false);
  const [supSel, setSupSel] = useState("todas");
  const [busca, setBusca] = useState("");
  const [filtroDia, setFiltroDia] = useState(null);     // null | 1..5 | "sin"
  const [historial, setHistorial] = useState(null);     // null cerrado | {titulo, filas|null}
  const [hoy, setHoy] = useState("");
  const [desde, setDesde] = useState("");               // «vigente desde» de los cambios que se van a guardar

  const cargar = useCallback(() => {
    setError("");
    axios.get("/api/ruta3/pisos")
      .then((r) => { setPisos(r.data.pisos || []); setCambios({}); setHoy(r.data.hoy || ""); setDesde(r.data.hoy || ""); })
      .catch((e) => setError(e.response?.data?.error || "No se pudieron cargar los pisos."));
  }, []);
  useEffect(() => { cargar(); }, [cargar]);

  useEffect(() => {
    if (!aviso) return undefined;
    const t = setTimeout(() => setAviso(""), 6000);
    return () => clearTimeout(t);
  }, [aviso]);

  const supervisoras = useMemo(() => [...new Set((pisos || []).map((p) => p.user_supervisora).filter(Boolean))].sort((a, b) => a.localeCompare(b, "es")), [pisos]);
  const diasDe = (p) => (cambios[p.id] != null ? cambios[p.id] : p.dias);

  const base = useMemo(() => (pisos || []).filter((p) => esSupervisora || supSel === "todas" || p.user_supervisora === supSel), [pisos, esSupervisora, supSel]);
  const conteo = useMemo(() => {
    const c = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, sin: 0 };
    base.forEach((p) => { diasDe(p).forEach((n) => { c[n] += 1; }); if (!p.organizado && cambios[p.id] == null) c.sin += 1; });
    return c;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [base, cambios]);
  const filas = useMemo(() => {
    const q = busca.trim().toLowerCase();
    return base.filter((p) => (!q || `${p.abreviatura} ${p.asistenta || ""} ${p.user_supervisora || ""}`.toLowerCase().includes(q))
      && (filtroDia == null || (filtroDia === "sin" ? (!p.organizado && cambios[p.id] == null) : diasDe(p).includes(filtroDia))));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [base, busca, filtroDia, cambios]);

  // Agrupado por el PRIMER día guardado de cada piso (no salta mientras editas) y, dentro del día, por la hora de entrada de la asistenta
  const grupos = useMemo(() => DIAS.map((d) => ({
    d,
    filas: filas.filter((p) => Math.min(...p.dias) === d.n).sort((a, b) => {
      const ha = a.horario?.[d.n]?.[0] || "99:99", hb = b.horario?.[d.n]?.[0] || "99:99";
      return ha.localeCompare(hb) || String(a.abreviatura).localeCompare(String(b.abreviatura), "es", { numeric: true });
    }),
  })).filter((g) => g.filas.length), [filas]);

  // marcar/desmarcar un día del piso (se pueden marcar varios; siempre al menos uno)
  const alternar = (p, n) => setCambios((c) => {
    const actuales = c[p.id] != null ? c[p.id] : p.dias;
    const nuevos = actuales.includes(n) ? actuales.filter((x) => x !== n) : [...actuales, n].sort();
    if (!nuevos.length) return c;                                   // no se puede dejar sin ningún día
    const resto = { ...c };
    if (p.organizado && mismosDias(nuevos, p.dias)) delete resto[p.id];   // vuelve a lo guardado
    else resto[p.id] = nuevos;                                      // (confirmar el miércoles por defecto también cuenta como organizar)
    return resto;
  });
  const nCambios = Object.keys(cambios).length;

  const guardar = async () => {
    if (!nCambios) return;
    setGuardando(true); setError("");
    try {
      const { data } = await axios.post("/api/ruta3/guardar", { cambios: Object.entries(cambios).map(([id, dias]) => ({ id_piso: Number(id), dias })), vigente_desde: desde || undefined });
      setAviso(`✅ Guardado: ${data.guardados} piso${data.guardados === 1 ? "" : "s"} cambiado${data.guardados === 1 ? "" : "s"} (vale desde el ${fmtFecha(data.vigente_desde)})${data.sin_cambios ? ` · ${data.sin_cambios} sin cambios` : ""}.`);
      cargar();
    } catch (e) {
      setError(e.response?.data?.error || "No se pudo guardar.");
    } finally { setGuardando(false); }
  };

  const abrirHistorial = (p) => {
    setHistorial({ titulo: p ? `🕘 Historial · ${p.abreviatura}` : "🕘 Historial de cambios", filas: null });
    axios.get("/api/ruta3/historial", { params: p ? { id_piso: p.id } : {} })
      .then((r) => setHistorial((h) => h && { ...h, filas: r.data.historial || [] }))
      .catch(() => setHistorial((h) => h && { ...h, filas: [], error: "No se pudo cargar el historial." }));
  };

  return (
    <div style={{ minHeight: "100vh", background: "#f1f5f9", fontFamily: "Segoe UI, sans-serif", paddingBottom: nCambios ? 90 : 20 }}>
      <div style={{ background: "#1e3a5f", color: "#fff", padding: "14px 18px", display: "flex", alignItems: "center", gap: 12 }}>
        <button onClick={() => navigate(-1)} style={st.btnCab}>← Volver</button>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, opacity: 0.7 }}>Servicios · días de visita de cada piso</div>
          <strong style={{ fontSize: 20 }}>🗓️ {esSupervisora ? "Organizar Ruta 3" : "Organización Ruta 3"}</strong>
        </div>
        <button onClick={() => abrirHistorial(null)} style={st.btnCab}>🕘 Historial</button>
      </div>

      <div style={{ padding: "14px 16px", maxWidth: 1000, margin: "0 auto" }}>
        <div style={{ background: "#ecfeff", border: "1px solid #a5f3fc", color: "#155e75", borderRadius: 12, padding: "10px 14px", fontSize: 13.5, marginBottom: 12 }}>
          Marca el <b>día o los días</b> (de lunes a viernes) en que visitas cada piso de ruta 3. Los pisos sin organizar siguen en <b>miércoles</b>.
          Cada cambio vale <b>desde la fecha que elijas</b> (por defecto hoy): los meses anteriores conservan los días que tenían y todo queda en el historial.
          Dentro de cada día los pisos salen por la hora de entrada de la asistenta.
        </div>

        {error && <div style={{ background: "#fee2e2", color: "#991b1b", borderRadius: 10, padding: 12, marginBottom: 10 }}>{error}</div>}
        {aviso && <div style={{ background: "#dcfce7", color: "#166534", borderRadius: 10, padding: 12, marginBottom: 10, fontWeight: 700 }}>{aviso}</div>}

        {/* Cuántos pisos hay cada día (pulsa para FILTRAR) */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
          {DIAS.map((d) => (
            <button key={d.n} onClick={() => setFiltroDia(filtroDia === d.n ? null : d.n)} title={`Ver solo los pisos de ${d.largo}`} style={{
              ...st.diaCard, borderColor: filtroDia === d.n ? d.color : "#e2e8f0", background: filtroDia === d.n ? `${d.color}14` : "#fff" }}>
              <span style={{ fontSize: 12, fontWeight: 800, color: d.color, textTransform: "capitalize" }}>{d.largo}</span>
              <span style={{ fontSize: 24, fontWeight: 900, color: "#1e293b" }}>{pisos ? conteo[d.n] : "…"}</span>
            </button>
          ))}
          <button onClick={() => setFiltroDia(filtroDia === "sin" ? null : "sin")} title="Ver solo los pisos sin organizar" style={{
            ...st.diaCard, borderColor: filtroDia === "sin" ? "#b45309" : "#e2e8f0", background: filtroDia === "sin" ? "#fef3c7" : "#fff" }}>
            <span style={{ fontSize: 12, fontWeight: 800, color: "#b45309" }}>Sin organizar</span>
            <span style={{ fontSize: 24, fontWeight: 900, color: "#1e293b" }}>{pisos ? conteo.sin : "…"}</span>
          </button>
        </div>

        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", marginBottom: 10 }}>
          {!esSupervisora && supervisoras.length > 0 && (
            <select value={supSel} onChange={(e) => setSupSel(e.target.value)} style={st.select}>
              <option value="todas">👩‍💼 Todas las supervisoras</option>
              {supervisoras.map((x) => <option key={x} value={x}>{x}</option>)}
            </select>
          )}
          <input value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="🔍 Buscar piso o asistenta…"
            style={{ flex: 1, minWidth: 190, padding: "9px 12px", borderRadius: 12, border: "2px solid #c7d2fe", fontSize: 13.5, outline: "none" }} />
          {filtroDia != null && <button onClick={() => setFiltroDia(null)} style={{ ...st.chip, borderColor: "#fca5a5", color: "#b91c1c" }}>✕ Quitar filtro</button>}
        </div>

        {!pisos ? <div style={{ textAlign: "center", padding: 40, color: "#64748b" }}>Cargando…</div> : (
          <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #e2e8f0", overflow: "hidden" }}>
            {filas.length === 0 && (
              <div style={{ padding: 30, textAlign: "center", color: "#94a3b8" }}>
                {pisos.length === 0 ? (esSupervisora ? "No tienes pisos de ruta 3." : "No hay pisos de ruta 3.") : "No hay pisos con estos filtros."}
              </div>
            )}
            {grupos.map((g) => (
              <div key={g.d.n}>
                <div style={{ position: "sticky", top: 0, zIndex: 1, background: `${g.d.color}12`, color: g.d.color, fontWeight: 900, padding: "7px 14px", borderBottom: "1px solid #e2e8f0", textTransform: "capitalize", fontSize: 14 }}>
                  {g.d.largo} <span style={{ fontWeight: 600, opacity: 0.8, textTransform: "none" }}>· {g.filas.length} piso{g.filas.length !== 1 ? "s" : ""} · por hora de entrada de la asistenta</span>
                </div>
                {g.filas.map((p, i) => {
                  const dd = diasDe(p);
                  const cambiado = cambios[p.id] != null;
                  const sc = p.servicio ? getServicioStyle(p.servicio) : null;
                  return (
                    <div key={p.id} style={{ display: "flex", alignItems: "flex-start", gap: 10, flexWrap: "wrap", padding: "9px 14px",
                      background: cambiado ? "#fffbeb" : i % 2 ? "#fafafa" : "#fff", borderBottom: "1px solid #f1f5f9" }}>
                      <div style={{ flex: "1 1 270px", minWidth: 210 }}>
                        <div style={{ fontWeight: 800, color: "#1e293b" }}>{p.abreviatura}</div>
                        <div style={{ fontSize: 11.5, color: "#94a3b8" }}>
                          {!esSupervisora && supSel === "todas" && p.user_supervisora ? `${p.user_supervisora} · ` : ""}{p.asistenta || "—"}
                        </div>
                        <div style={{ fontSize: 12, color: "#475569", marginTop: 3, display: "flex", flexWrap: "wrap", gap: 4, alignItems: "center" }}>
                          <span title="Alumnos del piso: A / R" style={st.chipAR}>
                            👥 <b style={{ color: "#1d4ed8" }}>A {p.alumnos?.a ?? 0}</b> · <b style={{ color: "#b45309" }}>R {p.alumnos?.r ?? 0}</b>
                          </span>
                          {sc && <span title="Plan (servicio) del piso" style={{ fontSize: 11.5, fontWeight: 800, borderRadius: 999, padding: "1px 9px", background: sc.bg, color: sc.color, border: `1px solid ${sc.border}` }}>Plan {p.servicio}</span>}
                        </div>
                        <div style={{ fontSize: 12, color: "#475569", marginTop: 3, display: "flex", flexWrap: "wrap", gap: "2px 10px", alignItems: "center" }}>
                          {dd.map((n) => {
                            const hor = p.horario?.[n];
                            return (
                              <span key={n}>
                                <span style={{ fontWeight: 800, color: dia(n).color, textTransform: "capitalize" }}>{dia(n).largo.slice(0, 3)}</span>{" "}
                                🕐 {hor ? `${hor[0] || "--"}–${hor[1] || "--"}` : "sin horario"}
                              </span>
                            );
                          })}
                          <PlanDia plan={p.plan} dia={dd[0]} />
                        </div>
                      </div>
                      <div style={{ display: "inline-flex", gap: 6 }}>
                        {DIAS.map((d) => {
                          const sel = dd.includes(d.n);
                          const implicito = sel && !p.organizado && !cambiado;   // miércoles por defecto, sin organizar
                          return (
                            <button key={d.n} onClick={() => alternar(p, d.n)} title={`${sel ? "Quitar" : "Añadir"} ${d.largo}`} style={{
                              width: 38, height: 38, borderRadius: "50%", fontWeight: 900, fontSize: 14, cursor: "pointer",
                              border: `2px ${implicito ? "dashed" : "solid"} ${sel ? d.color : "#cbd5e1"}`,
                              background: sel && !implicito ? d.color : implicito ? `${d.color}1f` : "#fff",
                              color: sel && !implicito ? "#fff" : sel ? d.color : "#94a3b8",
                              boxShadow: cambiado && sel ? "0 0 0 3px #fde68a" : "none",
                            }}>{d.l}</button>
                          );
                        })}
                      </div>
                      <div style={{ flex: "1 1 170px", fontSize: 12, color: cambiado ? "#b45309" : "#94a3b8", minWidth: 140 }}>
                        {cambiado ? `Sin guardar · ahora: ${nombreDias(p.organizado ? p.dias : null)}`
                          : p.organizado ? `Organizado por ${p.modificado_por || "—"} · ${fmtFechaHora(p.fecha_modificacion)}` : "Por defecto (miércoles)"}
                        {p.proximo && !cambiado && (
                          <div style={{ color: "#0369a1", fontWeight: 700 }}>📅 Desde el {fmtFecha(p.proximo.desde)} pasa a {nombreDias(p.proximo.dias)}</div>
                        )}
                      </div>
                      <button onClick={() => abrirHistorial(p)} title="Ver historial de este piso" style={st.btnIcono}>🕘</button>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        )}
      </div>

      {nCambios > 0 && (
        <div style={{ position: "fixed", left: 0, right: 0, bottom: "var(--fichaje-h, 0px)", zIndex: 100, background: "#1e293b", color: "#fff", padding: "12px 16px",
          display: "flex", alignItems: "center", gap: 12, justifyContent: "center", flexWrap: "wrap", boxShadow: "0 -4px 16px rgba(0,0,0,0.2)" }}>
          <strong>{nCambios} cambio{nCambios !== 1 ? "s" : ""} sin guardar</strong>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "rgba(255,255,255,0.1)", borderRadius: 10, padding: "4px 8px", flexWrap: "wrap" }}>
            <span style={{ fontSize: 13 }}>📅 Vale desde</span>
            <input type="date" value={desde} onChange={(e) => e.target.value && setDesde(e.target.value)} disabled={guardando}
              min={hoy ? sumarDias(hoy, -62) : undefined} max={hoy ? sumarDias(hoy, 180) : undefined}
              style={{ border: "none", borderRadius: 6, padding: "4px 6px", fontWeight: 700 }} />
            {hoy && [["Hoy", hoy], ["Lunes próximo", proximoLunes(hoy)], ["1 del mes que viene", primeroMesSiguiente(hoy)]].map(([t, f]) => (
              <button key={t} onClick={() => setDesde(f)} disabled={guardando}
                style={{ border: "none", borderRadius: 999, padding: "4px 10px", fontSize: 12, fontWeight: 800, cursor: "pointer", background: desde === f ? "#38bdf8" : "rgba(255,255,255,0.18)", color: desde === f ? "#0c4a6e" : "#fff" }}>{t}</button>
            ))}
          </span>
          {hoy && desde && desde !== hoy && (
            <span style={{ fontSize: 12, color: "#fde68a", flexBasis: "100%", textAlign: "center" }}>
              {desde > hoy ? "Hasta esa fecha cada piso sigue con sus días actuales." : "⚠️ Cambia también el pasado desde esa fecha (queda registrado en el historial)."}
            </span>
          )}
          <button onClick={() => setCambios({})} disabled={guardando} style={{ ...st.btnCab, background: "rgba(255,255,255,0.15)" }}>Descartar</button>
          <button onClick={guardar} disabled={guardando} style={{ border: "none", borderRadius: 10, padding: "10px 22px", fontWeight: 900, fontSize: 15, cursor: "pointer", background: "#22c55e", color: "#fff", opacity: guardando ? 0.6 : 1 }}>
            {guardando ? "Guardando…" : "💾 Guardar cambios"}
          </button>
        </div>
      )}

      {historial && (
        <div onClick={() => setHistorial(null)} style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.5)", zIndex: 2500, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 16, width: "min(620px, 100%)", maxHeight: "85vh", overflowY: "auto", padding: "16px 18px", boxShadow: "0 20px 50px rgba(0,0,0,0.3)" }}>
            <div style={{ display: "flex", alignItems: "center", marginBottom: 10 }}>
              <div style={{ flex: 1, fontSize: 18, fontWeight: 900, color: "#1e3a5f" }}>{historial.titulo}</div>
              <button onClick={() => setHistorial(null)} style={{ border: "none", background: "#f1f5f9", borderRadius: 8, padding: "6px 10px", cursor: "pointer", fontWeight: 800 }}>✕</button>
            </div>
            {historial.error && <div style={{ color: "#b91c1c" }}>{historial.error}</div>}
            {!historial.filas && !historial.error && <div style={{ color: "#94a3b8", padding: 20, textAlign: "center" }}>Cargando…</div>}
            {historial.filas && historial.filas.length === 0 && !historial.error && <div style={{ color: "#94a3b8", padding: 20, textAlign: "center" }}>Todavía no hay cambios guardados.</div>}
            {historial.filas && historial.filas.map((h) => (
              <div key={h.id} style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", border: "1px solid #eef2f7", borderRadius: 12, padding: "8px 12px", marginBottom: 6 }}>
                <strong style={{ color: "#1e293b" }}>{h.abreviatura}</strong>
                <span style={{ fontSize: 13.5, color: "#475569" }}>
                  <span>{h.dias_anterior ? nombreDias(h.dias_anterior) : "miércoles (por defecto)"}</span>
                  {" → "}<b style={{ color: "#1e3a5f" }}>{nombreDias(h.dias_nuevo)}</b>
                </span>
                <span style={{ marginLeft: "auto", fontSize: 11.5, color: "#94a3b8", textAlign: "right" }}>
                  desde {fmtFecha(h.vigente_desde)} · {h.usuario || "—"} · {fmtFechaHora(h.fecha_hora)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const st = {
  btnCab: { background: "rgba(255,255,255,0.15)", border: "none", borderRadius: 8, color: "#fff", fontWeight: 700, cursor: "pointer", padding: "8px 14px" },
  diaCard: { display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 2, minWidth: 104, padding: "8px 14px", borderRadius: 14, cursor: "pointer", border: "2px solid #e2e8f0", background: "#fff" },
  select: { padding: "9px 12px", borderRadius: 12, border: "2px solid #c7d2fe", fontSize: 13.5, fontWeight: 700, color: "#3730a3", background: "#fff", cursor: "pointer", outline: "none" },
  chip: { border: "2px solid #c7d2fe", background: "#fff", color: "#3730a3", borderRadius: 999, padding: "6px 12px", fontSize: 12.5, fontWeight: 800, cursor: "pointer" },
  chipPlan: { fontSize: 11.5, fontWeight: 700, color: "#334155", background: "#f1f5f9", border: "1px solid #e2e8f0", borderRadius: 999, padding: "1px 8px", whiteSpace: "nowrap" },
  chipAR: { fontSize: 11.5, fontWeight: 700, color: "#334155", background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 999, padding: "1px 9px", whiteSpace: "nowrap" },
  btnIcono: { border: "none", background: "#f1f5f9", borderRadius: 8, padding: "6px 9px", cursor: "pointer", fontSize: 14 },
};

export default OrganizarRuta3;
